import React, { useState, useEffect, useCallback } from 'react';
import './CommonStyles.css';
import { MapPin, Plus, Edit2, Settings, Power, PowerOff, X, Search, MapPinned, CreditCard, Tag, FileText, Printer } from 'lucide-react';
import apiService from '../../services/api';
import { toast } from 'react-toastify';

const ACCENT = '#6366f1';
const ACCENT_RGB = '99, 102, 241';

const customInputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    border: '1px solid var(--etpos-border, #e2e8f0)',
    background: 'var(--etpos-bg, #ffffff)',
    color: 'var(--etpos-text, #334155)',
    fontSize: '0.875rem',
    outline: 'none'
};

const modalOverlayStyle = {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
};

const modalContentStyle = {
    background: 'var(--etpos-card, #ffffff)',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
};

const defaultForm = {
    name: '', location_id: '', landmark: '', city: '', state: '', country: '', zip_code: '',
    mobile: '', alternate_number: '', email: '', website: '',
    invoice_scheme_id: '', sale_invoice_scheme_id: '', invoice_layout_id: '', sale_invoice_layout_id: '',
    selling_price_group_id: '', custom_field1: '', custom_field2: '', custom_field3: '', custom_field4: '',
    default_payment_accounts: {}, featured_products: []
};

const defaultSettingsForm = {
    print_receipt_on_invoice: '1', receipt_printer_type: 'browser', printer_id: '',
    invoice_layout_id: '', invoice_scheme_id: ''
};

const useWindowSize = () => {
    const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    useEffect(() => {
        const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return size;
};

const BusinessLocations = () => {
    const { width: winWidth } = useWindowSize();
    const isMobile = winWidth <= 768;
    const isSmallMobile = winWidth <= 480;

    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [settingsLocationId, setSettingsLocationId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    const [invoiceLayouts, setInvoiceLayouts] = useState({});
    const [invoiceSchemes, setInvoiceSchemes] = useState({});
    const [priceGroups, setPriceGroups] = useState({});
    const [paymentTypes, setPaymentTypes] = useState({});
    const [accounts, setAccounts] = useState({});
    const [customLabels, setCustomLabels] = useState({});
    const [printers, setPrinters] = useState({});
    const [printReceiptOptions, setPrintReceiptOptions] = useState({});
    const [receiptPrinterTypes, setReceiptPrinterTypes] = useState({});

    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);

    const [form, setForm] = useState({ ...defaultForm });
    const [settingsForm, setSettingsForm] = useState({ ...defaultSettingsForm });

    const filteredLocations = React.useMemo(() => {
        if (!searchTerm.trim()) return locations;
        const term = searchTerm.toLowerCase();
        return locations.filter(loc =>
            (loc.name || '').toLowerCase().includes(term) ||
            (loc.location_id || '').toLowerCase().includes(term) ||
            (loc.city || '').toLowerCase().includes(term) ||
            (loc.state || '').toLowerCase().includes(term) ||
            (loc.country || '').toLowerCase().includes(term) ||
            (loc.landmark || '').toLowerCase().includes(term)
        );
    }, [locations, searchTerm]);

    const fetchLocations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiService.getBusinessLocations({ include_inactive: '1' });
            if (res.success && Array.isArray(res.data)) setLocations(res.data);
            else setLocations([]);
        } catch (err) {
            toast.error('Failed to load business locations');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchLocations(); }, [fetchLocations]);

    const handleOpenAdd = async () => {
        setEditingId(null);
        setForm({ ...defaultForm });
        try {
            const res = await apiService.getBusinessLocationCreateData();
            if (res.success && res.data) {
                const d = res.data;
                setInvoiceLayouts(d.invoice_layouts || {});
                setInvoiceSchemes(d.invoice_schemes || {});
                setPriceGroups(d.price_groups || {});
                setPaymentTypes(d.payment_types || {});
                setAccounts(d.accounts || {});
                setCustomLabels(d.custom_labels || {});
                const firstLayout = Object.keys(d.invoice_layouts || {})[0] || '';
                const firstScheme = Object.keys(d.invoice_schemes || {})[0] || '';
                setForm(prev => ({ ...prev, invoice_layout_id: firstLayout, sale_invoice_layout_id: firstLayout, invoice_scheme_id: firstScheme, sale_invoice_scheme_id: firstScheme }));
                setShowModal(true);
            } else {
                toast.error(res.msg || res.message || 'Failed to load form data');
            }
        } catch (err) { toast.error(err?.message || 'Failed to load form data'); }
    };

    const handleOpenEdit = async (location) => {
        setEditingId(location.id);
        setForm({ ...defaultForm });
        try {
            const res = await apiService.getBusinessLocationEditData(location.id);
            if (res.success && res.data) {
                const d = res.data;
                const loc = d.location || {};
                setInvoiceLayouts(d.invoice_layouts || {});
                setInvoiceSchemes(d.invoice_schemes || {});
                setPriceGroups(d.price_groups || {});
                setPaymentTypes(d.payment_types || {});
                setAccounts(d.accounts || {});
                setCustomLabels(d.custom_labels || {});
                let dpAccounts = {};
                if (loc.default_payment_accounts) {
                    try { dpAccounts = typeof loc.default_payment_accounts === 'string' ? JSON.parse(loc.default_payment_accounts) : loc.default_payment_accounts; } catch (e) { dpAccounts = {}; }
                }
                let fp = [];
                if (loc.featured_products) {
                    try { fp = typeof loc.featured_products === 'string' ? JSON.parse(loc.featured_products) : (Array.isArray(loc.featured_products) ? loc.featured_products : []); } catch (e) { fp = []; }
                }
                setForm({
                    name: loc.name || '', location_id: loc.location_id || '', landmark: loc.landmark || '',
                    city: loc.city || '', state: loc.state || '', country: loc.country || '', zip_code: loc.zip_code || '',
                    mobile: loc.mobile || '', alternate_number: loc.alternate_number || '', email: loc.email || '', website: loc.website || '',
                    invoice_scheme_id: String(loc.invoice_scheme_id || ''), sale_invoice_scheme_id: String(loc.sale_invoice_scheme_id || ''),
                    invoice_layout_id: String(loc.invoice_layout_id || ''), sale_invoice_layout_id: String(loc.sale_invoice_layout_id || ''),
                    selling_price_group_id: loc.selling_price_group_id ? String(loc.selling_price_group_id) : '',
                    custom_field1: loc.custom_field1 || '', custom_field2: loc.custom_field2 || '', custom_field3: loc.custom_field3 || '', custom_field4: loc.custom_field4 || '',
                    default_payment_accounts: dpAccounts, featured_products: fp
                });
                setShowModal(true);
            } else { toast.error('Failed to load location data'); }
        } catch (err) { toast.error('Failed to load location data'); }
    };

    const handleOpenSettings = async (location) => {
        setSettingsLocationId(location.id);
        setSettingsForm({ ...defaultSettingsForm });
        try {
            const res = await apiService.getLocationSettings(location.id);
            if (res.success && res.data) {
                const d = res.data;
                setPrinters(d.printers || {});
                setPrintReceiptOptions(d.print_receipt_on_invoice || { '1': 'Yes', '0': 'No' });
                setReceiptPrinterTypes(d.receipt_printer_type || { browser: 'Browser Based Printing', printer: 'Configured Printer' });
                setInvoiceLayouts(d.invoice_layouts || {});
                setInvoiceSchemes(d.invoice_schemes || {});
                const loc = d.location || {};
                setSettingsForm({
                    print_receipt_on_invoice: String(loc.print_receipt_on_invoice ?? '1'),
                    receipt_printer_type: loc.receipt_printer_type || 'browser',
                    printer_id: loc.printer_id ? String(loc.printer_id) : '',
                    invoice_layout_id: String(loc.invoice_layout_id || ''),
                    invoice_scheme_id: String(loc.invoice_scheme_id || '')
                });
                setShowSettingsModal(true);
            } else { toast.error('Failed to load location settings'); }
        } catch (err) { toast.error('Failed to load location settings'); }
    };

    const handleSave = async () => {
        if (!form.name || !form.city || !form.state || !form.country || !form.zip_code) {
            toast.error('Please fill in all required fields (Name, City, State, Country, Zip Code)'); return;
        }
        if (form.location_id) {
            try {
                const isUnique = await apiService.checkBusinessLocationId(form.location_id, editingId);
                if (!isUnique) { toast.error('Location ID already exists'); return; }
            } catch (err) { /* continue */ }
        }
        setIsSaving(true);
        const toastId = toast.loading(editingId ? 'Updating...' : 'Creating...');
        try {
            const payload = { ...form };
            if (!payload.selling_price_group_id) delete payload.selling_price_group_id;
            if (editingId) {
                await apiService.updateBusinessLocation(editingId, payload);
                toast.update(toastId, { render: 'Business location updated successfully', type: 'success', isLoading: false, autoClose: 3000 });
            } else {
                await apiService.createBusinessLocation(payload);
                toast.update(toastId, { render: 'Business location added successfully', type: 'success', isLoading: false, autoClose: 3000 });
            }
            setShowModal(false); fetchLocations();
        } catch (err) {
            toast.update(toastId, { render: err.message || 'Failed to save', type: 'error', isLoading: false, autoClose: 5000 });
        } finally { setIsSaving(false); }
    };

    const handleSaveSettings = async () => {
        setIsSaving(true);
        const toastId = toast.loading('Updating receipt settings...');
        try {
            await apiService.updateLocationSettings(settingsLocationId, settingsForm);
            toast.update(toastId, { render: 'Receipt settings updated successfully', type: 'success', isLoading: false, autoClose: 3000 });
            setShowSettingsModal(false); fetchLocations();
        } catch (err) {
            toast.update(toastId, { render: err.message || 'Failed to update settings', type: 'error', isLoading: false, autoClose: 5000 });
        } finally { setIsSaving(false); }
    };

    const handleToggleActive = async (location) => {
        try {
            const res = await apiService.activateDeactivateBusinessLocation(location.id);
            if (res.success) { toast.success(res.message); fetchLocations(); }
            else toast.error(res.message || 'Failed');
        } catch (err) { toast.error('Failed to update location status'); }
    };

    const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
    const updateSettingsField = (field, value) => setSettingsForm(prev => ({ ...prev, [field]: value }));
    const updatePaymentAccount = (key, field, value) => setForm(prev => ({ ...prev, default_payment_accounts: { ...prev.default_payment_accounts, [key]: { ...prev.default_payment_accounts[key], [field]: value } } }));

    const handleProductSearch = async (term) => {
        setProductSearchTerm(term);
        if (!term || term.length < 2) { setProductSearchResults([]); setShowProductDropdown(false); return; }
        try {
            const res = await apiService.getProducts({ search: term, per_page: 10 });
            let products = [];
            if (Array.isArray(res)) products = res;
            else if (res.data && Array.isArray(res.data)) products = res.data;
            else if (res.meta && res.data) products = res.data;
            setProductSearchResults(products);
            setShowProductDropdown(true);
        } catch (err) { /* console.error(err); */ }
    };

    const addFeaturedProduct = (product) => {
        const id = String(product.id || product.product_id);
        if (!form.featured_products.includes(id)) setForm(prev => ({ ...prev, featured_products: [...prev.featured_products, id] }));
        setProductSearchTerm(''); setProductSearchResults([]); setShowProductDropdown(false);
    };
    const removeFeaturedProduct = (id) => setForm(prev => ({ ...prev, featured_products: prev.featured_products.filter(fp => fp !== id) }));

    const cfLabels = {
        custom_field1: customLabels?.location?.custom_field_1 || 'Custom Field 1',
        custom_field2: customLabels?.location?.custom_field_2 || 'Custom Field 2',
        custom_field3: customLabels?.location?.custom_field_3 || 'Custom Field 3',
        custom_field4: customLabels?.location?.custom_field_4 || 'Custom Field 4',
    };

    const renderForm = () => {
        const gridCols = isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))';
        const sectionHeader = (icon, title) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', fontWeight: '600', color: '#1e293b', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                {icon} {title}
            </div>
        );
        const fieldWrap = (label, input, required) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <label style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>
                    {label}{required && <span style={{ color: '#ef4444', marginLeft: '0.15rem' }}>*</span>}
                </label>
                {input}
            </div>
        );
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
                <div>
                    {sectionHeader(<MapPin size={18} style={{ color: ACCENT }} />, 'Basic Details')}
                    <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '1rem' }}>
                        {fieldWrap('Name', <input type="text" style={customInputStyle} value={form.name} onChange={e => updateField('name', e.target.value)} placeholder="Location name" />, true)}
                        {fieldWrap('Location ID', <input type="text" style={customInputStyle} value={form.location_id} onChange={e => updateField('location_id', e.target.value)} placeholder="Auto-generated if blank" />, false)}
                        {fieldWrap('Landmark', <input type="text" style={customInputStyle} value={form.landmark} onChange={e => updateField('landmark', e.target.value)} placeholder="Near landmark" />, false)}
                        {fieldWrap('City', <input type="text" style={customInputStyle} value={form.city} onChange={e => updateField('city', e.target.value)} placeholder="City" />, true)}
                        {fieldWrap('State', <input type="text" style={customInputStyle} value={form.state} onChange={e => updateField('state', e.target.value)} placeholder="State" />, true)}
                        {fieldWrap('Country', <input type="text" style={customInputStyle} value={form.country} onChange={e => updateField('country', e.target.value)} placeholder="Country" />, true)}
                        {fieldWrap('Zip Code', <input type="text" style={customInputStyle} value={form.zip_code} onChange={e => updateField('zip_code', e.target.value)} placeholder="Zip / Postal code" />, true)}
                        {fieldWrap('Mobile', <input type="text" style={customInputStyle} value={form.mobile} onChange={e => updateField('mobile', e.target.value)} placeholder="Contact number" />, false)}
                        {fieldWrap('Alternate Number', <input type="text" style={customInputStyle} value={form.alternate_number} onChange={e => updateField('alternate_number', e.target.value)} placeholder="Alternate contact" />, false)}
                        {fieldWrap('Email', <input type="email" style={customInputStyle} value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="email@example.com" />, false)}
                        {fieldWrap('Website', <input type="text" style={customInputStyle} value={form.website} onChange={e => updateField('website', e.target.value)} placeholder="https://example.com" />, false)}
                    </div>
                </div>

                <div>
                    {sectionHeader(<FileText size={18} style={{ color: ACCENT }} />, 'Invoice Settings')}
                    <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '1rem' }}>
                        {fieldWrap('Invoice Scheme for POS',
                            <select style={customInputStyle} value={form.invoice_scheme_id} onChange={e => updateField('invoice_scheme_id', e.target.value)}>
                                <option value="">Select scheme</option>
                                {Object.entries(invoiceSchemes).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, true)}
                        {fieldWrap('Invoice Scheme for Sale',
                            <select style={customInputStyle} value={form.sale_invoice_scheme_id} onChange={e => updateField('sale_invoice_scheme_id', e.target.value)}>
                                <option value="">Select scheme</option>
                                {Object.entries(invoiceSchemes).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, true)}
                        {fieldWrap('Invoice Layout for POS',
                            <select style={customInputStyle} value={form.invoice_layout_id} onChange={e => updateField('invoice_layout_id', e.target.value)}>
                                <option value="">Select layout</option>
                                {Object.entries(invoiceLayouts).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, true)}
                        {fieldWrap('Invoice Layout for Sale',
                            <select style={customInputStyle} value={form.sale_invoice_layout_id} onChange={e => updateField('sale_invoice_layout_id', e.target.value)}>
                                <option value="">Select layout</option>
                                {Object.entries(invoiceLayouts).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, true)}
                        {fieldWrap('Selling Price Group',
                            <select style={customInputStyle} value={form.selling_price_group_id} onChange={e => updateField('selling_price_group_id', e.target.value)}>
                                <option value="">None (default)</option>
                                {Object.entries(priceGroups).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, false)}
                    </div>
                </div>

                <div>
                    {sectionHeader(<Tag size={18} style={{ color: ACCENT }} />, 'Custom Fields')}
                    <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '1rem' }}>
                        {fieldWrap(cfLabels.custom_field1, <input type="text" style={customInputStyle} value={form.custom_field1} onChange={e => updateField('custom_field1', e.target.value)} />, false)}
                        {fieldWrap(cfLabels.custom_field2, <input type="text" style={customInputStyle} value={form.custom_field2} onChange={e => updateField('custom_field2', e.target.value)} />, false)}
                        {fieldWrap(cfLabels.custom_field3, <input type="text" style={customInputStyle} value={form.custom_field3} onChange={e => updateField('custom_field3', e.target.value)} />, false)}
                        {fieldWrap(cfLabels.custom_field4, <input type="text" style={customInputStyle} value={form.custom_field4} onChange={e => updateField('custom_field4', e.target.value)} />, false)}
                    </div>
                </div>

                <div>
                    {sectionHeader(<Search size={18} style={{ color: ACCENT }} />, 'Featured Products')}
                    <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                        <input type="text" style={customInputStyle} value={productSearchTerm} onChange={e => handleProductSearch(e.target.value)} placeholder="Type at least 2 characters to search products..." />
                        {showProductDropdown && productSearchResults.length > 0 && (
                            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--etpos-card, #ffffff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', maxHeight: '220px', overflowY: 'auto', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                {productSearchResults.map(product => (
                                    <div key={product.id || product.product_id} style={{ padding: '0.6rem 0.9rem', cursor: 'pointer', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', transition: 'background 0.1s' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--etpos-bg, #f8fafc)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'} onClick={() => addFeaturedProduct(product)}>
                                        {product.name || product.product_name || product.sku}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {form.featured_products.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {form.featured_products.map(fp => (
                                <span key={fp} style={{ background: 'rgba(99, 102, 241, 0.12)', color: ACCENT, padding: '0.35rem 0.7rem', borderRadius: '6px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', border: '1px solid rgba(99, 102, 241, 0.25)', fontWeight: '500' }}>
                                    {fp} <X size={14} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => removeFeaturedProduct(fp)} />
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #94a3b8)', fontStyle: 'italic' }}>No featured products selected.</div>
                    )}
                </div>

                <div>
                    {sectionHeader(<CreditCard size={18} style={{ color: ACCENT }} />, 'Default Payment Accounts')}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        {Object.entries(paymentTypes).map(([key, label]) => (
                            <div key={key} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '0.5rem' : '1rem', alignItems: isMobile ? 'stretch' : 'center', padding: '0.6rem 0.8rem', borderRadius: '8px', background: 'var(--etpos-bg, #f8fafc)', border: '1px solid var(--etpos-border, #e2e8f0)' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--etpos-text, #334155)', fontWeight: '500', flexShrink: 0, minWidth: '140px' }}>
                                    <input type="checkbox" checked={!!form.default_payment_accounts[key]?.is_enabled} onChange={e => updatePaymentAccount(key, 'is_enabled', e.target.checked ? 1 : 0)} style={{ width: '18px', height: '18px', accentColor: ACCENT, cursor: 'pointer' }} />
                                    {label}
                                </label>
                                {form.default_payment_accounts[key]?.is_enabled ? (
                                    <select style={{ ...customInputStyle, flex: 1 }} value={form.default_payment_accounts[key]?.account || ''} onChange={e => updatePaymentAccount(key, 'account', e.target.value)}>
                                        <option value="">Select Account</option>
                                        {Object.entries(accounts).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                                    </select>
                                ) : (
                                    <div style={{ flex: 1, fontSize: '0.8rem', color: 'var(--etpos-text-muted, #94a3b8)', padding: '0.35rem 0' }}>Enable to assign an account</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderSettingsForm = () => {
        const gridCols = isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))';
        const fieldWrap = (label, input, required) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '500' }}>
                    {label}{required && <span style={{ color: '#ef4444', marginLeft: '0.15rem' }}>*</span>}
                </label>
                {input}
            </div>
        );
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                        <Printer size={18} style={{ color: ACCENT }} /> Receipt Settings
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '1rem' }}>
                        {fieldWrap('Print receipt on invoice',
                            <select style={customInputStyle} value={settingsForm.print_receipt_on_invoice} onChange={e => updateSettingsField('print_receipt_on_invoice', e.target.value)}>
                                {Object.entries(printReceiptOptions).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, false)}
                        {fieldWrap('Receipt Printer Type',
                            <select style={customInputStyle} value={settingsForm.receipt_printer_type} onChange={e => updateSettingsField('receipt_printer_type', e.target.value)}>
                                {Object.entries(receiptPrinterTypes).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, true)}
                        {settingsForm.receipt_printer_type === 'printer' && (
                            fieldWrap('Printer',
                                <select style={customInputStyle} value={settingsForm.printer_id} onChange={e => updateSettingsField('printer_id', e.target.value)}>
                                    <option value="">Select printer</option>
                                    {Object.entries(printers).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                                </select>, true)
                        )}
                        {fieldWrap('Invoice Layout',
                            <select style={customInputStyle} value={settingsForm.invoice_layout_id} onChange={e => updateSettingsField('invoice_layout_id', e.target.value)}>
                                <option value="">Select layout</option>
                                {Object.entries(invoiceLayouts).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, true)}
                        {fieldWrap('Invoice Scheme',
                            <select style={customInputStyle} value={settingsForm.invoice_scheme_id} onChange={e => updateSettingsField('invoice_scheme_id', e.target.value)}>
                                <option value="">Select scheme</option>
                                {Object.entries(invoiceSchemes).map(([id,name]) => <option key={id} value={id}>{name}</option>)}
                            </select>, true)}
                    </div>
                </div>
            </div>
        );
    };

    const responsiveModalOverlay = {
        ...modalOverlayStyle,
        padding: isSmallMobile ? 0 : '1rem',
        alignItems: isSmallMobile ? 'stretch' : 'center'
    };

    const responsiveModalContent = {
        ...modalContentStyle,
        maxWidth: isSmallMobile ? '100%' : '900px',
        maxHeight: isSmallMobile ? '100vh' : '90vh',
        borderRadius: isSmallMobile ? 0 : '12px'
    };

    const statusBadge = (active) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.35rem 0.65rem',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: '500',
        background: active ? '#dcfce7' : '#fee2e2',
        color: active ? '#166534' : '#991b1b',
        border: `1px solid ${active ? '#86efac' : '#fecaca'}`
    });

    const actionBtn = (color, isPrimary = false) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.35rem',
        padding: isMobile ? '0.4rem 0.6rem' : '0.45rem 0.7rem',
        borderRadius: '6px',
        border: isPrimary ? 'none' : '1px solid var(--etpos-border, #e5e7eb)',
        background: isPrimary ? color : 'var(--etpos-card, #fff)',
        color: isPrimary ? '#fff' : (color || 'var(--etpos-text, #374151)'),
        cursor: 'pointer',
        fontSize: '0.8rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'all 0.15s'
    });

    return (
        <div className="module-container business-locations-wrapper" style={{ padding: isMobile ? '0.75rem' : '1.25rem' }}>
            {/* Main Card — matches Invoice Settings card style */}
            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                {/* Card Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div>
                        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: isMobile ? '1.1rem' : '1.35rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>
                            <MapPinned size={isMobile ? 20 : 26} style={{ color: ACCENT }} /> Business Locations
                        </h2>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)' }}>
                            {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''} found
                        </p>
                    </div>
                    <button onClick={handleOpenAdd} style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: '8px', padding: '0.6rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', boxShadow: '0 2px 6px rgba(0,0,0,0.12)', whiteSpace: 'nowrap' }}>
                        <Plus size={18} /> {isMobile ? 'Add' : 'Add Location'}
                    </button>
                </div>

                {/* Card Body */}
                <div style={{ padding: isMobile ? '1rem' : '1.5rem' }}>
                    {/* Search Bar */}
                    <div style={{ position: 'relative', marginBottom: '1.25rem', maxWidth: '420px' }}>
                        <Search size={16} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                        <input
                            type="text"
                            placeholder="Search by name, ID, city, state..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.65rem 0.85rem 0.65rem 2.4rem',
                                borderRadius: '8px',
                                border: '1px solid var(--etpos-border, #e2e8f0)',
                                background: 'var(--etpos-bg, #ffffff)',
                                color: 'var(--etpos-text, #334155)',
                                fontSize: '0.875rem',
                                outline: 'none'
                            }}
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0.2rem' }}>
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#94a3b8' }}>
                    <div style={{ display: 'inline-block', width: '28px', height: '28px', border: '3px solid #e5e7eb', borderTopColor: ACCENT, borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '0.75rem' }} />
                    <div style={{ fontSize: '0.9rem' }}>Loading locations...</div>
                </div>
            ) : (
                <>
                    {/* Desktop Table */}
                    {!isMobile && (
                        <div style={{ background: 'var(--etpos-card, #ffffff)', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left' }}>
                                    <thead>
                                        <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '2px solid var(--etpos-border, #e2e8f0)' }}>
                                            {['Name','Location ID','Landmark','City','Zip Code','State','Country','Price Group','Invoice Scheme','Invoice Layout for POS','Invoice Layout for Sale','Action'].map(h => (
                                                <th key={h} style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLocations.map((loc) => (
                                            <tr key={loc.id} style={{
                                                borderBottom: '1px solid var(--etpos-border, #f1f5f9)',
                                                background: 'var(--etpos-card, #ffffff)',
                                                transition: 'background 0.15s',
                                                cursor: 'pointer'
                                            }} onMouseEnter={e => e.currentTarget.style.background = 'var(--etpos-bg, #f8fafc)'}
                                               onMouseLeave={e => e.currentTarget.style.background = 'var(--etpos-card, #ffffff)'}
                                            >
                                                <td style={{ padding: '1rem' }}>
                                                    <div style={{ fontWeight: '600', color: 'var(--etpos-text, #1e293b)', fontSize: '0.9rem' }}>{loc.name}</div>
                                                </td>
                                                <td style={{ padding: '1rem', whiteSpace: 'nowrap' }}>
                                                    <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', background: 'var(--etpos-bg, #f1f5f9)', padding: '0.15rem 0.5rem', borderRadius: '4px', color: 'var(--etpos-text, #475569)' }}>{loc.location_id}</span>
                                                </td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.landmark || '-'}</td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{loc.city || '-'}</td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{loc.zip_code || '-'}</td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{loc.state || '-'}</td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{loc.country || '-'}</td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{loc.price_group || '-'}</td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{loc.invoice_scheme || '-'}</td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{loc.invoice_layout || '-'}</td>
                                                <td style={{ padding: '1rem', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{loc.sale_invoice_layout || '-'}</td>
                                                <td style={{ padding: '0.75rem 1rem' }}>
                                                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                                                        <button onClick={() => handleOpenEdit(loc)} title="Edit" style={actionBtn(ACCENT, true)} onMouseEnter={e => { e.currentTarget.style.background = '#4f46e5'; e.currentTarget.style.transform = 'translateY(-1px)'; }} onMouseLeave={e => { e.currentTarget.style.background = ACCENT; e.currentTarget.style.transform = 'none'; }}>
                                                            <Edit2 size={14} />
                                                        </button>
                                                        <button onClick={() => handleOpenSettings(loc)} title="Receipt Settings" style={actionBtn('var(--etpos-text-muted, #6b7280)')} onMouseEnter={e => { e.currentTarget.style.background = 'var(--etpos-bg, #f3f4f6)'; e.currentTarget.style.borderColor = 'var(--etpos-border, #d1d5db)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'var(--etpos-card, #fff)'; e.currentTarget.style.borderColor = 'var(--etpos-border, #e5e7eb)'; }}>
                                                            <Settings size={14} />
                                                        </button>
                                                        <button onClick={() => handleToggleActive(loc)} title={loc.is_active ? 'Deactivate' : 'Activate'} style={actionBtn(loc.is_active ? '#ef4444' : '#22c55e', true)} onMouseEnter={e => { e.currentTarget.style.background = loc.is_active ? '#dc2626' : '#16a34a'; e.currentTarget.style.transform = 'translateY(-1px)'; }} onMouseLeave={e => { e.currentTarget.style.background = loc.is_active ? '#ef4444' : '#22c55e'; e.currentTarget.style.transform = 'none'; }}>
                                                            {loc.is_active ? <PowerOff size={14} /> : <Power size={14} />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {filteredLocations.length === 0 && (
                                <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: 'var(--etpos-text-muted, #94a3b8)' }}>
                                    <MapPin size={36} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
                                    <div style={{ fontSize: '0.95rem', fontWeight: '500', color: 'var(--etpos-text-muted, #64748b)' }}>No locations found</div>
                                    <div style={{ fontSize: '0.8rem', marginTop: '0.3rem', color: 'var(--etpos-text-muted, #94a3b8)' }}>Try adjusting your search or add a new location.</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Mobile Cards */}
                    {isMobile && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {filteredLocations.map(loc => (
                                <div key={loc.id} style={{
                                    background: 'var(--etpos-card, #ffffff)',
                                    border: '1px solid var(--etpos-border, #e2e8f0)',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.6rem',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: '600', color: 'var(--etpos-text, #1e293b)', fontSize: '1rem', marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.name}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', background: 'var(--etpos-bg, #f1f5f9)', padding: '0.15rem 0.4rem', borderRadius: '4px', color: 'var(--etpos-text, #475569)' }}>{loc.location_id}</span>
                                                {loc.city && <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)' }}>{loc.city}</span>}
                                            </div>
                                        </div>
                                        <span style={statusBadge(loc.is_active)}>
                                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: loc.is_active ? '#22c55e' : '#ef4444', display: 'inline-block' }} />
                                            {loc.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem 0.75rem', fontSize: '0.75rem' }}>
                                        {loc.landmark && <span style={{ color: 'var(--etpos-text-muted, #64748b)' }}>{loc.landmark}</span>}
                                        {loc.zip_code && <span style={{ color: 'var(--etpos-text-muted, #64748b)' }}>{loc.zip_code}</span>}
                                        {loc.state && <span style={{ color: 'var(--etpos-text-muted, #64748b)' }}>{loc.state}</span>}
                                        {loc.country && <span style={{ color: 'var(--etpos-text-muted, #64748b)' }}>{loc.country}</span>}
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                                        {loc.price_group && <span style={{ fontSize: '0.7rem', background: 'rgba(67, 56, 202, 0.1)', color: '#4338ca', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>{loc.price_group}</span>}
                                        {loc.invoice_scheme && <span style={{ fontSize: '0.7rem', background: 'rgba(22, 101, 52, 0.1)', color: '#22c55e', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>{loc.invoice_scheme}</span>}
                                        {loc.invoice_layout && <span style={{ fontSize: '0.7rem', background: 'rgba(154, 52, 18, 0.1)', color: '#f97316', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>POS: {loc.invoice_layout}</span>}
                                        {loc.sale_invoice_layout && <span style={{ fontSize: '0.7rem', background: 'rgba(157, 23, 77, 0.1)', color: '#ec4899', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>Sale: {loc.sale_invoice_layout}</span>}
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                                        <button onClick={() => handleOpenEdit(loc)} style={{ flex: 1, ...actionBtn(ACCENT, true), justifyContent: 'center', padding: '0.5rem' }}>
                                            <Edit2 size={15} /> Edit
                                        </button>
                                        <button onClick={() => handleOpenSettings(loc)} style={{ flex: 1, ...actionBtn('var(--etpos-text-muted, #6b7280)'), justifyContent: 'center', padding: '0.5rem' }}>
                                            <Settings size={15} /> Settings
                                        </button>
                                        <button onClick={() => handleToggleActive(loc)} style={{ flex: 1, ...actionBtn(loc.is_active ? '#ef4444' : '#22c55e', true), justifyContent: 'center', padding: '0.5rem' }}>
                                            {loc.is_active ? <><PowerOff size={15} /> Deactivate</> : <><Power size={15} /> Activate</>}
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {filteredLocations.length === 0 && (
                                <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: 'var(--etpos-text-muted, #94a3b8)' }}>
                                    <MapPin size={40} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
                                    <div style={{ fontSize: '1rem', fontWeight: '500', color: 'var(--etpos-text-muted, #64748b)' }}>No locations found</div>
                                    <div style={{ fontSize: '0.85rem', marginTop: '0.3rem', color: 'var(--etpos-text-muted, #94a3b8)' }}>Try adjusting your search or add a new location.</div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
                </div>
            </div>

            {/* Add / Edit Modal */}
            {showModal && (
                <div style={responsiveModalOverlay} onClick={() => !isSaving && setShowModal(false)}>
                    <div style={responsiveModalContent} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: isSmallMobile ? '1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <h3 style={{ margin: 0, fontSize: isSmallMobile ? '1.1rem' : '1.25rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <MapPin size={20} style={{ color: ACCENT }} /> {editingId ? 'Edit Business Location' : 'Add Business Location'}
                            </h3>
                            <button onClick={() => setShowModal(false)} disabled={isSaving} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--etpos-text-muted, #94a3b8)', padding: '0.3rem', fontSize: '1.5rem', lineHeight: '1' }}>
                                <X size={24} />
                            </button>
                        </div>
                        <div style={{ padding: isSmallMobile ? '1rem' : '1.25rem', overflowY: 'auto', flex: 1 }}>
                            {renderForm()}
                        </div>
                        <div style={{ padding: isSmallMobile ? '0.85rem 1rem' : '1rem 1.25rem', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'center', gap: '0.75rem', flexShrink: 0 }}>
                            <button onClick={() => setShowModal(false)} disabled={isSaving} style={{ padding: '0.6rem 1.1rem', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)', background: 'var(--etpos-bg, #f8fafc)', color: 'var(--etpos-text, #334155)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>Close</button>
                            <button onClick={handleSave} disabled={isSaving} style={{ padding: '0.6rem 1.1rem', borderRadius: '8px', border: 'none', background: ACCENT, color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}>
                                {isSaving ? 'Saving...' : 'Save Location'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Settings Modal */}
            {showSettingsModal && (
                <div style={responsiveModalOverlay} onClick={() => !isSaving && setShowSettingsModal(false)}>
                    <div style={{ ...responsiveModalContent, maxWidth: isSmallMobile ? '100%' : '560px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: isSmallMobile ? '1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <h3 style={{ margin: 0, fontSize: isSmallMobile ? '1.1rem' : '1.25rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Printer size={20} style={{ color: ACCENT }} /> Location Settings
                            </h3>
                            <button onClick={() => setShowSettingsModal(false)} disabled={isSaving} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--etpos-text-muted, #94a3b8)', padding: '0.3rem', fontSize: '1.5rem', lineHeight: '1' }}>
                                <X size={24} />
                            </button>
                        </div>
                        <div style={{ padding: isSmallMobile ? '1rem' : '1.25rem', overflowY: 'auto', flex: 1 }}>
                            {renderSettingsForm()}
                        </div>
                        <div style={{ padding: isSmallMobile ? '0.85rem 1rem' : '1rem 1.25rem', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'center', gap: '0.75rem', flexShrink: 0 }}>
                            <button onClick={() => setShowSettingsModal(false)} disabled={isSaving} style={{ padding: '0.6rem 1.1rem', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)', background: 'var(--etpos-bg, #f8fafc)', color: 'var(--etpos-text, #334155)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>Close</button>
                            <button onClick={handleSaveSettings} disabled={isSaving} style={{ padding: '0.6rem 1.1rem', borderRadius: '8px', border: 'none', background: ACCENT, color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}>
                                {isSaving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusinessLocations;
